/**
 * FILE-OVERVIEW:
 * - 役割: メモ画面のカレンダー表示とビュー切替を管理。
 * - 主な連携: MemoNavigation.js, MemoContent.js
 * - 本番運用上の注意: モバイル表示時のツールバー抑制ルールに注意する。
 */

import React, { useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { Box, CircularProgress } from '@mui/material';
import { scheduleEventColors, badgeColors, publishingStatusColors, mediaStatusColors } from '../../utils/colors';
import { groupContentsByTheme } from '../../services/s3Service';
import { toDateKey } from '../../utils/meetingSheet';
import type { MemoContent, ThemeGroup } from '../../services/s3Service';
import type { EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core';

/** カレンダーのイベントから元のテーマを引くための通し番号付きテーマ */
type ThemeWithIndex = ThemeGroup & { themeIndex: number };

interface MemoCalendarMainProps {
  isMobile: boolean;
  selectedDate?: Date | null;
  onDateSelect: (date: Date, theme?: ThemeGroup) => void;
  /** 「他n件」クリック時。現状このコンポーネントからは呼んでいない */
  onShowMore?: (date: Date, themes: ThemeGroup[]) => void;
  calendarRef: React.RefObject<FullCalendar | null>;
  filteredContents: MemoContent[];
  loading?: boolean;
}

const SHARED_CALENDAR_VIEW_KEY = 'sharedCalendarView';

// 掲載状態の優先順位（高い方が優先：降版済 > 掲載済み > 出稿済み > 未掲載）
const PUBLISHING_STATUS_PRIORITY: Record<string, number> = {
  '降版済み': 4,
  '掲載済み': 3,
  '出稿済み': 2,
  '未掲載': 1,
};

// 派生状態の優先順位（高い方が優先：派生済 > 未派生）
const MEDIA_STATUS_PRIORITY: Record<string, number> = {
  '派生済': 2,
  '未派生': 1,
};

// テーマ内の掲載状態を集約（最も優先度の高い状態を1つ返す）
const aggregatePublishingStatus = (contents: MemoContent[]): string => {
  let best = '';
  let bestPriority = 0;
  contents.forEach((c) => {
    const status = (c.publishingStatus || '').replace(/\s/g, '');
    const priority = PUBLISHING_STATUS_PRIORITY[status] || 0;
    if (priority > bestPriority) {
      bestPriority = priority;
      best = status;
    }
  });
  return best;
};

// テーマ内の派生状態を集約（最も優先度の高い状態を1つ返す）
const aggregateMediaStatus = (contents: MemoContent[]): string => {
  let best = '';
  let bestPriority = 0;
  contents.forEach((c) => {
    const status = (c.mediaStatus || '').replace(/\s/g, '');
    const priority = MEDIA_STATUS_PRIORITY[status] || 0;
    if (priority > bestPriority) {
      bestPriority = priority;
      best = status;
    }
  });
  return best;
};

const MemoCalendarMain = ({ isMobile, selectedDate, onDateSelect, onShowMore, calendarRef, filteredContents, loading }: MemoCalendarMainProps) => {
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const previousViewTypeRef = React.useRef<string | null>(null);
  const storedView = sessionStorage.getItem(SHARED_CALENDAR_VIEW_KEY)
    || sessionStorage.getItem('memoCalendarView');
  const initialView = isMobile
    ? (storedView === 'dayGridMonth' || storedView === 'dayGridWeek' ? 'dayGridDay' : (storedView || 'dayGridDay'))
    : (storedView || 'dayGridMonth');

  // テーマ単位にグループ化
  const themeGroups = useMemo(() => {
    return groupContentsByTheme(filteredContents);
  }, [filteredContents]);

  // 日付ごとにテーマをグループ化
  const themesByDate = useMemo(() => {
    const byDate: Record<string, ThemeWithIndex[]> = {};
    themeGroups.forEach((theme, index) => {
      // dateKey が無いテーマは、従来どおり "undefined" というキーにまとまる
      const dateKey = String(theme.dateKey);
      if (!byDate[dateKey]) {
        byDate[dateKey] = [];
      }
      byDate[dateKey]!.push({ ...theme, themeIndex: index });
    });
    // 各日付内のテーマを作成日時の降順（最新順）に並べ替え。
    // 日時の書式が "YYYY/MM/DD hh:mm:ss" と "YYYYMMDD" で混在しうるため数字だけで比較し、
    // 同時刻はMemoIDで決着させて並びが毎回変わらないようにする
    const sortKey = (value: unknown): string => String(value || '').replace(/[^\d]/g, '').padEnd(14, '0');
    Object.keys(byDate).forEach((dateKey) => {
      byDate[dateKey]!.sort((a, b) => {
        const diff = sortKey(b.firstCreatedDate).localeCompare(sortKey(a.firstCreatedDate));
        return diff !== 0 ? diff : String(a.memoId || '').localeCompare(String(b.memoId || ''));
      });
    });
    return byDate;
  }, [themeGroups]);

  // イベントクリック時のハンドラー
  const handleEventClick = (info: EventClickArg) => {
    // extendedProps はイベント生成時に themeIndex を入れている
    const themeIndex = info.event.extendedProps?.themeIndex as number | undefined;
    if (themeIndex !== undefined) {
      const theme = themeGroups[themeIndex];
      if (theme) {
        onDateSelect(new Date(theme.dateKey as string), theme);
      }
    }
  };

  // カレンダーに表示するイベントを作成（全件表示）
  const events: EventInput[] = [];
  Object.keys(themesByDate).forEach(dateKey => {
    const themes = themesByDate[dateKey]!;

    themes.forEach((theme, index) => {
      const genre = theme.newsGenre || '';
      const pubStatus = aggregatePublishingStatus(theme.contents);
      const mediaStatus = aggregateMediaStatus(theme.contents);

      // 紐づく記事の種類別枚数を集計
      const memoKindCounts = { photo: 0, eps: 0, pdf: 0 };
      theme.contents.forEach(c => {
        const kind = c.memoKind || '';
        if (kind === '写真') memoKindCounts.photo++;
        else if (kind === 'EPS' || kind === 'ＥＰＳ') memoKindCounts.eps++;
        else if (kind === 'PDF' || kind === 'ＰＤＦ') memoKindCounts.pdf++;
      });

      events.push({
        id: `theme-${dateKey}-${index}`,
        title: theme.themeName || '(テーマ名なし)',
        start: dateKey,
        allDay: true,
        backgroundColor: scheduleEventColors.background,
        borderColor: scheduleEventColors.border,
        order: index,
        extendedProps: {
          themeIndex: theme.themeIndex,
          dateKey: dateKey,
          themeName: theme.themeName || '(テーマ名なし)',
          newsGenre: genre,
          publishingStatus: pubStatus,
          mediaStatus: mediaStatus,
          firstCreatedDate: theme.firstCreatedDate || '',
          memoKindCounts,
        }
      });
    });

  });

  // チップスタイル
  const chipStyle: React.CSSProperties = {
    display: 'inline-block',
    fontSize: '0.7rem',
    lineHeight: 1.2,
    padding: '2px 6px',
    borderRadius: '3px',
    color: '#fff',
    marginRight: '2px',
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
  };

  // カスタムイベントレンダリング
  const renderEventContent = (eventInfo: EventContentArg) => {
    const { extendedProps } = eventInfo.event;

    // スマホ月・週表示はシンプルなバー表示
    if (isMobile && (currentView === 'dayGridMonth' || currentView === 'dayGridWeek')) {
      return (
        <div style={{ width: '100%', height: '18px' }} />
      );
    }

    const newsGenre = extendedProps?.newsGenre;
    const publishingStatus = extendedProps?.publishingStatus;
    const mediaStatus = extendedProps?.mediaStatus;
    const themeName = extendedProps?.themeName || eventInfo.event.title;
    const firstCreatedDate = extendedProps?.firstCreatedDate || '';
    const memoKindCounts = extendedProps?.memoKindCounts || {};

    return (
      <div style={{
        padding: '6px 8px',
        overflow: 'hidden',
        width: '100%',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.3,
        fontSize: '0.85rem',
        fontWeight: 500,
        color: '#000000',
      }}>
        {/* バッジ行 */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '2px',
          marginBottom: '4px',
        }}>
          {newsGenre && (
            <span style={{ ...chipStyle, backgroundColor: badgeColors.newsGenre }}>
              {newsGenre}
            </span>
          )}
          {publishingStatus && (
            <span style={{ ...chipStyle, backgroundColor: publishingStatusColors[publishingStatus] || badgeColors.publishingStatus }}>
              {publishingStatus.charAt(0)}
            </span>
          )}
          {mediaStatus && mediaStatus !== '未派生' && (
            <span style={{ ...chipStyle, backgroundColor: mediaStatusColors[mediaStatus] || badgeColors.mediaStatus }}>
              {mediaStatus.charAt(0)}
            </span>
          )}
        </div>
        {/* テーマ名（最大20文字） */}
        <div style={{
          overflow: 'hidden',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          marginBottom: '2px',
        }}>
          {themeName.length > 20 ? themeName.slice(0, 20) + '…' : themeName}
        </div>
        {/* 記事種類別枚数 */}
        {(memoKindCounts.photo > 0 || memoKindCounts.eps > 0 || memoKindCounts.pdf > 0) && (
          <div style={{
            display: 'flex',
            gap: '4px',
            fontSize: '0.65rem',
            marginBottom: '2px',
          }}>
            {memoKindCounts.photo > 0 && (
              <span style={{ ...chipStyle, backgroundColor: '#1e88e5', fontSize: '0.65rem', padding: '1px 4px' }}>
                写真:{memoKindCounts.photo}
              </span>
            )}
            {memoKindCounts.eps > 0 && (
              <span style={{ ...chipStyle, backgroundColor: '#43a047', fontSize: '0.65rem', padding: '1px 4px' }}>
                EPS:{memoKindCounts.eps}
              </span>
            )}
            {memoKindCounts.pdf > 0 && (
              <span style={{ ...chipStyle, backgroundColor: '#e53935', fontSize: '0.65rem', padding: '1px 4px' }}>
                PDF:{memoKindCounts.pdf}
              </span>
            )}
          </div>
        )}
        {/* 作成日時 */}
        {firstCreatedDate && (
          <div style={{
            fontSize: '0.7rem',
            color: '#555',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}>
            作成:{firstCreatedDate}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      className="memo-calendar-main-container"
      sx={{
        flex: 1,
        p: 2,
        pb: isMobile ? 'calc(var(--mobile-footer-nav-space) + env(safe-area-inset-bottom))' : 2,
        maxWidth: 1680,
        width: '100%',
        mx: 'auto',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& .fc .fc-header-toolbar': {
          display: isMobile ? 'none' : 'flex',
        },
      }}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
        initialView={initialView}
        initialDate={sessionStorage.getItem('calendarDate') || undefined}
        events={events}
        locale={jaLocale}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        selectable={true}
        height="100%"
        headerToolbar={isMobile
          ? { left: '', center: 'title', right: '' }
          : { left: 'prev,next today', center: 'title', right: 'meetingSheet dayGridMonth,dayGridWeek,dayGridDay,listWeek' }
        }
        customButtons={{
          meetingSheet: {
            text: '会議資料',
            hint: '会議資料を新しいタブで開く',
            // 日表示ならその日、それ以外は今日の資料を開く。日付は資料画面側でも切り替えられる
            click: () => {
              const view = calendarRef.current?.getApi().view;
              const date = view?.type === 'dayGridDay' ? view.currentStart : new Date();
              window.open(`/memo/meeting?date=${toDateKey(date)}`, '_blank', 'noopener');
            },
          },
        }}
        eventMinHeight={46}
        dayMaxEventRows={false}
        eventDisplay="block"
        eventOrder="order"
        datesSet={(info) => {
          const previousViewType = previousViewTypeRef.current;
          const currentViewType = info.view.type;
          const isViewModeChanged = previousViewType && previousViewType !== currentViewType;
          previousViewTypeRef.current = currentViewType;

          if (isViewModeChanged) {
            info.view.calendar.gotoDate(new Date());
          }

          setCurrentView(info.view.type);
          sessionStorage.setItem('memoCalendarView', info.view.type);
          sessionStorage.setItem(SHARED_CALENDAR_VIEW_KEY, info.view.type);
          sessionStorage.setItem('calendarDate', info.view.currentStart.toISOString());
        }}
        dayHeaderContent={(args) => {
          if (isMobile && args.view?.type === 'dayGridWeek') {
            const date = new Date(args.date);
            return date.getDate();
          }
          if (args.view?.type.startsWith('list')) {
            const date = new Date(args.date);
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const weekday = date.toLocaleDateString('ja-JP', { weekday: 'narrow' });
            return `${month}月${day}日（${weekday}）`;
          }
          return args.text;
        }}
      />
    </Box>
  );
};

export default MemoCalendarMain;
